// src/modules/competitions/pages/JudgeParticipantEvaluation.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../../../shared/components/ui/Card';
import Button from '../../../shared/components/ui/Button';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import JudgeEvaluationModal from '../components/JudgeEvaluationModal';
import JudgeVideoEvaluationModal from '../components/JudgeVideoEvaluationModal';
import { ParticipantDetails, EvaluableItem } from '../types/judge';
import { getFullImageUrl } from '../../../shared/services/apiConfig';
import '../styles/judgeParticipantEvaluation.css';

const JudgeParticipantEvaluation: React.FC = () => {
  const { eventId, participantId } = useParams<{ eventId: string; participantId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const passedData = location.state?.participantDetails as ParticipantDetails | undefined;
  
  const [participantDetails, setParticipantDetails] = useState<ParticipantDetails | null>(
    passedData || null
  );
  const [error, setError] = useState<string | null>(null);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  // Modal states
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<EvaluableItem | null>(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);

  useEffect(() => {
    if (!passedData) {
      navigate(`/competitions/judge/${eventId}/evaluations`, { replace: true });
    }
  }, [passedData, eventId, navigate]);

  const hasValidImage = (item: EvaluableItem): boolean => {
    return item.type === 'image' && !!item.image_url;
  };

  // ✅ NUEVA FUNCIÓN: Construir URL del video
  const buildVideoUrl = (item: EvaluableItem): string => {
    if (!participantDetails) return '';
    
    // Construir la URL del endpoint PHP con los parámetros correctos
    const baseUrl = '/api/api/v1/competitions/players/video/get-participant-video.php';
    const params = new URLSearchParams({
      participant_id: participantDetails.participant.participant_id.toString(),
      video_type_id: item.video_type_id?.toString() || ''
    });
    
    return `${baseUrl}?${params.toString()}`;
  };

  const handleItemClick = async (item: EvaluableItem, index: number) => {
    if (!participantDetails) return;
    
    setSelectedItem(item);
    setCurrentItemIndex(index);
    
    if (item.type === 'image') {
      setShowImageModal(true);
    } else {
      setShowVideoModal(true);
    }
  };

  const handleNavigateItem = (direction: 'prev' | 'next') => {
    if (!participantDetails) return;

    const items = participantDetails.items;
    let newIndex = currentItemIndex;

    if (direction === 'prev') {
      newIndex = currentItemIndex > 0 ? currentItemIndex - 1 : items.length - 1;
    } else {
      newIndex = currentItemIndex < items.length - 1 ? currentItemIndex + 1 : 0;
    }

    const newItem = items[newIndex];
    setCurrentItemIndex(newIndex);
    setSelectedItem(newItem);

    if (newItem.type !== selectedItem?.type) {
      setShowImageModal(false);
      setShowVideoModal(false);

      setTimeout(() => {
        if (newItem.type === 'image') {
          setShowImageModal(true);
        } else {
          setShowVideoModal(true);
        }
      }, 100);
    }
  };

  const handleCloseModal = () => {
    setShowImageModal(false);
    setShowVideoModal(false);
    setSelectedItem(null);
  };

  const handleEvaluationComplete = () => {
    // Actualizar estado si es necesario
  };

  const handleCloseEvaluation = () => {
    if (!participantDetails) return;

    if (participantDetails.statistics.is_fully_evaluated) {
      navigate(`/competitions/judge/${eventId}/evaluations`);
    } else {
      alert('Debes evaluar todas las imágenes y videos antes de cerrar la evaluación.');
    }
  };

  const handleImageLoad = (index: number) => {
    setLoadedImages(prev => new Set(prev).add(index));
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>, index: number) => {
    e.currentTarget.style.display = 'none';
    setLoadedImages(prev => new Set(prev).add(index));
  };

  if (!participantDetails) {
    return (
      <Card variant="flat">
        <ErrorMessage message="No se pudo cargar la información del participante" type="error" />
      </Card>
    );
  }

  const { participant, items, statistics } = participantDetails;
  
  if (!statistics) {
    console.error('Statistics is undefined:', participantDetails);
    return (
      <Card variant="flat">
        <ErrorMessage message="Error: Datos incompletos del participante" type="error" />
      </Card>
    );
  }
  
  const progress = statistics.total_items > 0
    ? (statistics.evaluated_items / statistics.total_items) * 100
    : 0;

  return (
    <div className="judge-participant-evaluation-page">
      {/* Header */}
      <Card variant="glass" className="evaluation-header">
        <div className="header-navigation">
          <Button
            variant="outline"
            onClick={() => navigate(`/competitions/judge/${eventId}/evaluations`)}
          >
            ← Volver
          </Button>
        </div>

        <div className="header-info">
          <h1>{participant.participant_code}</h1>
          <h2>{participant.category_name} - {participant.level}</h2>
          <p className="event-title">{participant.event_title}</p>
        </div>

        <div className="evaluation-progress">
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="progress-stats">
            <span className="progress-text">
              {statistics.evaluated_items}/{statistics.total_items} evaluados
            </span>
            <span className="progress-percentage">
              {Math.round(progress)}%
            </span>
          </div>
        </div>

        {error && <ErrorMessage message={error} type="error" onClose={() => setError(null)} />}
      </Card>

      {/* Collage */}
      <div className="evaluation-collage-grid">
        {items.map((item, index) => (
          <div
            key={`${item.type}-${item.submission_id}`}
            className="collage-item"
            onClick={() => handleItemClick(item, index)}
          >
            <div className="collage-item-media">
              {item.type === 'image' ? (
                <div className="image-container">
                  {!loadedImages.has(index) && hasValidImage(item) && (
                    <div className="collage-loading">
                      <div className="spinner"></div>
                    </div>
                  )}
                  
                  {hasValidImage(item) ? (
                    <img
                      src={getFullImageUrl(item.image_url) ?? undefined}
                      alt={item.image_type_name}
                      className="collage-image"
                      loading="lazy"
                      decoding="async"
                      onLoad={() => handleImageLoad(index)}
                      onError={(e) => handleImageError(e, index)}
                      style={{
                        opacity: loadedImages.has(index) ? 1 : 0,
                        transition: 'opacity 0.3s ease-in-out'
                      }}
                    />
                  ) : (
                    <div className="collage-placeholder">
                      🖼️
                    </div>
                  )}
                </div>
              ) : (
                <div className="collage-video-placeholder">
                  <span className="video-icon">🎥</span>
                  <span className="video-duration">
                    {item.duration_seconds}s
                  </span>
                </div>
              )}
            </div>

            <div className="collage-item-overlay">
              <div className={`collage-badge ${item.is_evaluated ? 'evaluated' : 'pending'}`}>
                {item.is_evaluated ? '✓' : '⏳'}
              </div>

              <div className="collage-info">
                <span className="collage-type">
                  {item.type === 'image' ? item.image_type_name : item.video_type_name}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Botón Cerrar Evaluación */}
      <Card variant="flat" className="evaluation-footer">
        <Button
          variant={statistics.is_fully_evaluated ? 'success' : 'outline'}
          size="lg"
          onClick={handleCloseEvaluation}
          disabled={!statistics.is_fully_evaluated}
          fullWidth
        >
          {statistics.is_fully_evaluated
            ? '✅ Cerrar Evaluación'
            : `⏳ Pendiente (${statistics.pending_items} restantes)`
          }
        </Button>

        {!statistics.is_fully_evaluated && (
          <p className="footer-hint">
            Evalúa todas las imágenes y videos para habilitar el botón de cerrar
          </p>
        )}
      </Card>

      {/* Modal de evaluación de imagen */}
      {showImageModal && selectedItem && selectedItem.type === 'image' && (
        <JudgeEvaluationModal
          isOpen={showImageModal}
          onClose={handleCloseModal}
          categoryId={participant.competition_category_id}
          level={participant.level}
          imageTypeId={selectedItem.image_type_id!}
          imageTypeName={selectedItem.image_type_name!}
          submissionId={selectedItem.submission_id}
          imageUrl={getFullImageUrl(selectedItem.image_url) ?? undefined}
          onEvaluationComplete={handleEvaluationComplete}
          onNavigate={handleNavigateItem}
          currentIndex={currentItemIndex + 1}
          totalItems={items.length}
        />
      )}

      {/* ✅ Modal de evaluación de video CORREGIDO */}
      {showVideoModal && selectedItem && selectedItem.type === 'video' && (
        <JudgeVideoEvaluationModal
          isOpen={showVideoModal}
          onClose={handleCloseModal}
          videoSubmissionId={selectedItem.submission_id}
  categoryId={participant.competition_category_id} // ✅ AGREGAR
  level={participant.level} // ✅ AGREGAR
          videoUrl={buildVideoUrl(selectedItem)} // ✅ Usar la nueva función
          videoTypeName={selectedItem.video_type_name!}
          participantCode={participant.participant_code}
          durationSeconds={selectedItem.duration_seconds || 0}
    videoTypeId={selectedItem.video_type_id!}
          onEvaluationComplete={handleEvaluationComplete}
          onNavigate={handleNavigateItem}
          currentIndex={currentItemIndex + 1}
          totalItems={items.length}
        />
      )}
    </div>
  );
};

export default JudgeParticipantEvaluation;